// pages/LeadForms.tsx
import React, { useEffect, useState } from 'react';
import {
  Plus, ClipboardList, Copy, Check, Eye, EyeOff, Edit, Trash2, Globe, Loader2, X, Settings2, RefreshCw,
  ArrowUp, ArrowDown, Layers, ListPlus, Type, CheckSquare, Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { leadFormsApi, LeadForm, LeadFormDesign, PublicLeadFormData, LeadFormStep, LeadFormField } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PublicLeadFormContent, DEFAULT_LEAD_FORM_STEPS } from '../components/PublicLeadFormContent';

const DEFAULT_DESIGN: LeadFormDesign = {
  templateId: 'modern',
  primaryColor: '#6366f1',
  accentColor: '#10b981',
  showLogo: true,
  darkMode: false,
  radius: 'rounded-xl',
  fieldStyle: 'filled',
};

const DESIGNS: { value: LeadFormDesign['templateId']; label: string; desc: string }[] = [
  { value: 'modern', label: 'Moderno', desc: 'Fundo escuro com destaque' },
  { value: 'classic', label: 'Clássico', desc: 'Limpo, fundo claro' },
  { value: 'minimal', label: 'Minimalista', desc: 'Mínimo e direto' },
  { value: 'vibrant', label: 'Vibrante', desc: 'Cores vivas em gradiente' },
];

const RADII: { value: LeadFormDesign['radius']; label: string }[] = [
  { value: 'rounded', label: 'Discreto' },
  { value: 'rounded-xl', label: 'Suave' },
  { value: 'rounded-2xl', label: 'Arredondado' },
  { value: 'rounded-full', label: 'Pill' },
];

const FIELD_STYLES: { value: LeadFormDesign['fieldStyle']; label: string }[] = [
  { value: 'filled', label: 'Preenchido' },
  { value: 'outlined', label: 'Contornado' },
  { value: 'underline', label: 'Sublinhado' },
];

const FIELD_TYPES: { value: LeadFormField['type']; label: string }[] = [
  { value: 'text', label: 'Texto Curto' },
  { value: 'textarea', label: 'Texto Longo (Parágrafo)' },
  { value: 'number', label: 'Número' },
  { value: 'phone', label: 'WhatsApp / Telefone' },
  { value: 'email', label: 'E-mail' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Seleção Única (Dropdown)' },
  { value: 'radio', label: 'Opções de Escolha (Radio)' },
  { value: 'multiselect', label: 'Seleção Múltipla (Checkboxes)' },
];

const TARGET_FIELDS = [
  { value: 'name', label: 'Nome do Lead (Obrigatório principal)' },
  { value: 'whatsapp', label: 'WhatsApp / Telefone (Obrigatório principal)' },
  { value: 'email', label: 'E-mail do Lead' },
  { value: 'companyName', label: 'Nome da Empresa' },
  { value: 'segment', label: 'Segmento de Negócio' },
  { value: 'challenges', label: 'Desafios Principais' },
  { value: 'currentProcess', label: 'Processo Atual' },
  { value: 'improvement', label: 'Prioridade de Melhoria' },
  { value: 'decisionMaker', label: 'Nome do Decisor' },
  { value: 'bestTime', label: 'Melhor Horário' },
  { value: 'channel', label: 'Como Conheceu' },
  { value: '', label: 'Campo Personalizado (Guarda nas notas)' },
];

const publicFormUrl = (slug?: string) =>
  slug ? `${window.location.origin}/form/${slug}` : '';

export const LeadForms: React.FC = () => {
  const { company } = useAuth();
  const [forms, setForms] = useState<LeadForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // Designer / preview
  const [editing, setEditing] = useState<LeadForm | null>(null);
  const [draft, setDraft] = useState<Partial<LeadForm>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Builder state
  const [activeTab, setActiveTab] = useState<'fields' | 'content' | 'design'>('fields');
  const [expandedStepIndex, setExpandedStepIndex] = useState<number>(0);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      const res = await leadFormsApi.getAll();
      setForms(res);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar formulários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Indique um nome para o formulário');
      return;
    }
    setCreating(true);
    try {
      const form = await leadFormsApi.create({ name: newName.trim() });
      setForms((prev) => [form, ...prev]);
      setCreateOpen(false);
      setNewName('');
      toast.success('Formulário criado! Agora pode desenhá-lo e publicá-lo.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao criar formulário');
    } finally {
      setCreating(false);
    }
  };

  const openEditor = (form: LeadForm) => {
    setEditing(form);
    const initialSteps = form.steps && form.steps.length > 0 ? JSON.parse(JSON.stringify(form.steps)) : JSON.parse(JSON.stringify(DEFAULT_LEAD_FORM_STEPS));
    setDraft({
      name: form.name,
      title: form.title,
      subtitle: form.subtitle,
      ctaText: form.ctaText,
      design: { ...DEFAULT_DESIGN, ...form.design },
      steps: initialSteps,
    });
    setActiveTab('fields');
    setExpandedStepIndex(0);
    setPreviewOpen(false);
  };

  const updateDraft = (key: keyof LeadForm, value: any) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const updateDesign = (key: keyof LeadFormDesign, value: any) => {
    setDraft((prev) => ({
      ...prev,
      design: { ...DEFAULT_DESIGN, ...prev.design, [key]: value },
    }));
  };

  // --- Funções de Manipulação de Passos (Steps) ---
  const addStep = () => {
    setDraft((prev) => {
      const currentSteps = prev.steps || [];
      const newStep: LeadFormStep = {
        id: 'step_' + Date.now(),
        title: `Passo ${currentSteps.length + 1}`,
        description: '',
        fields: [
          {
            id: 'field_' + Date.now(),
            label: 'Nova Pergunta',
            type: 'text',
            placeholder: '',
            required: false,
          },
        ],
      };
      return { ...prev, steps: [...currentSteps, newStep] };
    });
    setExpandedStepIndex((draft.steps?.length || 0));
  };

  const removeStep = (sIdx: number) => {
    setDraft((prev) => {
      const currentSteps = prev.steps || [];
      if (currentSteps.length <= 1) {
        toast.error('O formulário deve ter pelo menos 1 passo.');
        return prev;
      }
      const updated = currentSteps.filter((_, idx) => idx !== sIdx);
      return { ...prev, steps: updated };
    });
    setExpandedStepIndex(Math.max(0, sIdx - 1));
  };

  const moveStep = (sIdx: number, direction: 'up' | 'down') => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      const targetIdx = direction === 'up' ? sIdx - 1 : sIdx + 1;
      if (targetIdx < 0 || targetIdx >= currentSteps.length) return prev;
      const temp = currentSteps[sIdx];
      currentSteps[sIdx] = currentSteps[targetIdx];
      currentSteps[targetIdx] = temp;
      return { ...prev, steps: currentSteps };
    });
    setExpandedStepIndex(direction === 'up' ? sIdx - 1 : sIdx + 1);
  };

  const updateStep = (sIdx: number, key: keyof LeadFormStep, val: any) => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      if (!currentSteps[sIdx]) return prev;
      currentSteps[sIdx] = { ...currentSteps[sIdx], [key]: val };
      return { ...prev, steps: currentSteps };
    });
  };

  // --- Funções de Manipulação de Campos (Fields) ---
  const addField = (sIdx: number) => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      if (!currentSteps[sIdx]) return prev;
      const newField: LeadFormField = {
        id: 'field_' + Date.now(),
        label: 'Nova Pergunta',
        type: 'text',
        placeholder: '',
        required: false,
        options: ['Opção 1', 'Opção 2'],
      };
      currentSteps[sIdx] = {
        ...currentSteps[sIdx],
        fields: [...(currentSteps[sIdx].fields || []), newField],
      };
      return { ...prev, steps: currentSteps };
    });
  };

  const removeField = (sIdx: number, fIdx: number) => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      if (!currentSteps[sIdx]) return prev;
      const updatedFields = currentSteps[sIdx].fields.filter((_, idx) => idx !== fIdx);
      currentSteps[sIdx] = { ...currentSteps[sIdx], fields: updatedFields };
      return { ...prev, steps: currentSteps };
    });
  };

  const moveField = (sIdx: number, fIdx: number, direction: 'up' | 'down') => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      if (!currentSteps[sIdx]) return prev;
      const fields = [...currentSteps[sIdx].fields];
      const targetIdx = direction === 'up' ? fIdx - 1 : fIdx + 1;
      if (targetIdx < 0 || targetIdx >= fields.length) return prev;
      const temp = fields[fIdx];
      fields[fIdx] = fields[targetIdx];
      fields[targetIdx] = temp;
      currentSteps[sIdx] = { ...currentSteps[sIdx], fields };
      return { ...prev, steps: currentSteps };
    });
  };

  const updateField = (sIdx: number, fIdx: number, key: keyof LeadFormField, val: any) => {
    setDraft((prev) => {
      const currentSteps = [...(prev.steps || [])];
      if (!currentSteps[sIdx] || !currentSteps[sIdx].fields[fIdx]) return prev;
      const fields = [...currentSteps[sIdx].fields];
      fields[fIdx] = { ...fields[fIdx], [key]: val };
      currentSteps[sIdx] = { ...currentSteps[sIdx], fields };
      return { ...prev, steps: currentSteps };
    });
  };

  const updateFieldOptionsStr = (sIdx: number, fIdx: number, str: string) => {
    const opts = str.split(',').map((o) => o.trim()).filter(Boolean);
    updateField(sIdx, fIdx, 'options', opts);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await leadFormsApi.update(editing._id, {
        name: draft.name,
        title: draft.title,
        subtitle: draft.subtitle,
        ctaText: draft.ctaText,
        design: draft.design,
        steps: draft.steps,
      });
      setForms((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      setEditing(updated);
      toast.success('Alterações guardadas com sucesso!');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao guardar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (form: LeadForm) => {
    try {
      const updated = await leadFormsApi.toggle(form._id);
      setForms((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      if (editing?._id === updated._id) setEditing(updated);
      toast.success(updated.enabled ? 'Formulário ativado' : 'Formulário desativado');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao alternar estado');
    }
  };

  const handlePublish = async (form: LeadForm) => {
    try {
      const updated = await leadFormsApi.publish(form._id);
      setForms((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      if (editing?._id === updated._id) setEditing(updated);
      toast.success('Formulário publicado!');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao publicar');
    }
  };

  const handleUnpublish = async (form: LeadForm) => {
    try {
      const updated = await leadFormsApi.unpublish(form._id);
      setForms((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      if (editing?._id === updated._id) setEditing(updated);
      toast.success('Formulário despublicado');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao despublicar');
    }
  };

  const handleRegenerateSlug = async (form: LeadForm) => {
    try {
      const updated = await leadFormsApi.regenerateSlug(form._id);
      setForms((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      if (editing?._id === updated._id) setEditing(updated);
      toast.success('Novo link gerado');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao gerar novo link');
    }
  };

  const handleDelete = async (form: LeadForm) => {
    if (!window.confirm(`Remover o formulário "${form.name}"?`)) return;
    try {
      await leadFormsApi.remove(form._id);
      setForms((prev) => prev.filter((f) => f._id !== form._id));
      if (editing?._id === form._id) setEditing(null);
      toast.success('Formulário removido');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao remover formulário');
    }
  };

  const copyLink = async (slug?: string) => {
    const url = publicFormUrl(slug);
    if (!url) {
      toast.error('Publique primeiro para obter o link');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug || '');
      setTimeout(() => setCopiedSlug(null), 1500);
      toast.success('Link copiado!');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const buildPreviewData = (): PublicLeadFormData | null => {
    if (!editing || !company) return null;
    return {
      form: {
        _id: editing._id,
        name: draft.name || editing.name,
        title: draft.title || 'Vamos perceber como o MPV pode ajudar a sua empresa.',
        subtitle: draft.subtitle || '',
        ctaText: draft.ctaText || 'Quero conhecer o MPV',
        design: { ...DEFAULT_DESIGN, ...draft.design },
        steps: draft.steps || editing.steps || DEFAULT_LEAD_FORM_STEPS,
      },
      company: {
        _id: company._id || '',
        name: company.name,
        logo: company.logo,
        email: company.email,
      },
    };
  };

  const previewData = buildPreviewData();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="text-indigo-600" size={26} /> Formulários de Captação
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Crie formulários dinâmicos estilo Google Forms que caem diretamente no seu pipeline de vendas.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
        >
          <Plus size={18} /> Novo formulário
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-indigo-600" size={32} />
        </div>
      ) : forms.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center">
          <ClipboardList className="mx-auto text-slate-300" size={56} />
          <h3 className="mt-4 font-semibold text-slate-700">Ainda não há formulários</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Crie o primeiro formulário para captar leads diretamente da sua página pública.
          </p>
          <button
            onClick={() => setCreateOpen(true)}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            <Plus size={18} /> Criar primeiro formulário
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((form) => {
            const url = publicFormUrl(form.slug);
            return (
              <div key={form._id} className={`bg-white rounded-2xl border shadow-sm p-5 flex flex-col ${form.enabled && form.published ? 'border-green-200' : 'border-slate-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Globe size={18} className={form.published ? 'text-green-600' : 'text-slate-400'} />
                    <h3 className="font-semibold text-slate-800 truncate">{form.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        form.published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {form.published ? 'Publicado' : 'Rascunho'}
                    </span>
                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(form)}
                      className={`relative w-10 h-5 rounded-full transition ${form.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
                      title={form.enabled ? 'Desativar formulário' : 'Ativar formulário'}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition ${form.enabled ? 'left-5' : 'left-0.5'}`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                  <span>{form.submissionsCount || 0} submissões</span>
                  <span>{form.steps?.length || 3} passos</span>
                </div>

                {url && (
                  <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-lg px-2 py-1.5">
                    <span className="text-xs text-slate-500 truncate flex-1">{url}</span>
                    <button
                      onClick={() => copyLink(form.slug)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title="Copiar link"
                    >
                      {copiedSlug === form.slug ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-400 hover:text-slate-600"
                      title="Abrir formulário"
                    >
                      <Eye size={16} />
                    </a>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => openEditor(form)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100"
                  >
                    <Edit size={15} /> Desenhar
                  </button>
                  {form.published ? (
                    <button
                      onClick={() => handleUnpublish(form)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 font-medium hover:bg-slate-200"
                    >
                      <EyeOff size={15} /> Despublicar
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePublish(form)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-50 text-green-700 font-medium hover:bg-green-100"
                    >
                      <Globe size={15} /> Publicar
                    </button>
                  )}
                  <button
                    onClick={() => handleRegenerateSlug(form)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 font-medium hover:bg-slate-200"
                    title="Gerar novo link"
                  >
                    <RefreshCw size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(form)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100 ml-auto"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800 text-lg">Novo formulário</h3>
              <button onClick={() => setCreateOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">
              Nome interno do formulário
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex.: Captação de leads — site"
              className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setCreateOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
              >
                {creating && <Loader2 size={16} className="animate-spin" />} Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Designer modal (Google Forms Style Builder) */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex z-50">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl mx-auto flex flex-col max-h-[90vh]">
              {/* Header de ferramentas */}
              <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-slate-800 flex items-center gap-2 text-lg">
                    <Settings2 size={20} className="text-indigo-600" /> Designer de Formulário
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">Construa e personalize os passos e campos dinâmicos.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewOpen(!previewOpen)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
                  >
                    {previewOpen ? <Settings2 size={16} /> : <Eye size={16} />} {previewOpen ? 'Editar' : 'Preview'}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 transition shadow-sm"
                  >
                    {saving && <Loader2 size={16} className="animate-spin" />} Guardar
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="p-2 rounded-lg text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {previewOpen ? (
                <div className="max-h-[75vh] overflow-y-auto">
                  {previewData && (
                    <PublicLeadFormContent data={previewData} onSubmit={async () => {}} submitting={false} submitted={false} />
                  )}
                  <div className="bg-white p-4 text-center text-xs text-slate-400 border-t border-slate-200">
                    <strong className="text-slate-600">Link público:</strong> {publicFormUrl(editing.slug) || 'Publique para gerar o link'}
                    {editing.slug && (
                      <button onClick={() => copyLink(editing.slug)} className="ml-2 inline-flex items-center gap-1 text-indigo-600 font-medium">
                        {copiedSlug === editing.slug ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar link</>}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto flex flex-col">
                  {/* Tab Navigation */}
                  <div className="flex border-b border-slate-200 bg-slate-50/50 px-5 pt-3 gap-2">
                    <button
                      onClick={() => setActiveTab('fields')}
                      className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
                        activeTab === 'fields'
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Layers size={17} /> Passos & Campos (Google Form Builder)
                    </button>
                    <button
                      onClick={() => setActiveTab('content')}
                      className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
                        activeTab === 'content'
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Type size={17} /> Textos & CTA
                    </button>
                    <button
                      onClick={() => setActiveTab('design')}
                      className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
                        activeTab === 'design'
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Sparkles size={17} /> Aparência & Cores
                    </button>
                  </div>

                  <div className="p-6 flex-1 overflow-y-auto">
                    {/* TAB 1: PASSOS & CAMPOS (BUILDER DINÂMICO) */}
                    {activeTab === 'fields' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-bold text-slate-800 text-base">Passos do Formulário</h4>
                            <p className="text-xs text-slate-500">Organize as perguntas por etapas. Cada passo corresponde a um ecrã para o visitante.</p>
                          </div>
                          <button
                            onClick={addStep}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 text-indigo-700 rounded-lg font-semibold text-xs hover:bg-indigo-100 transition"
                          >
                            <Plus size={16} /> Adicionar Passo
                          </button>
                        </div>

                        {/* Lista de Passos */}
                        {(draft.steps || []).map((step, sIdx) => {
                          const isExpanded = expandedStepIndex === sIdx;
                          return (
                            <div key={step.id || sIdx} className="bg-slate-50/80 border border-slate-200 rounded-xl overflow-hidden transition shadow-sm">
                              {/* Step Bar Header */}
                              <div
                                onClick={() => setExpandedStepIndex(isExpanded ? -1 : sIdx)}
                                className="p-4 bg-slate-100/70 hover:bg-slate-100 flex items-center justify-between cursor-pointer"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                                    {sIdx + 1}
                                  </span>
                                  <div className="truncate">
                                    <h5 className="font-bold text-slate-800 text-sm truncate">{step.title || `Passo ${sIdx + 1}`}</h5>
                                    <p className="text-xs text-slate-500 truncate">{step.fields?.length || 0} perguntas configuradas</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => moveStep(sIdx, 'up')}
                                    disabled={sIdx === 0}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    title="Mover para cima"
                                  >
                                    <ArrowUp size={16} />
                                  </button>
                                  <button
                                    onClick={() => moveStep(sIdx, 'down')}
                                    disabled={sIdx === (draft.steps?.length || 1) - 1}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    title="Mover para baixo"
                                  >
                                    <ArrowDown size={16} />
                                  </button>
                                  <button
                                    onClick={() => removeStep(sIdx)}
                                    className="p-1.5 text-red-500 hover:text-red-700"
                                    title="Eliminar Passo"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => setExpandedStepIndex(isExpanded ? -1 : sIdx)}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 ml-1"
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </button>
                                </div>
                              </div>

                              {/* Body do Passo (Expanded) */}
                              {isExpanded && (
                                <div className="p-4 space-y-4 bg-white border-t border-slate-200">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-600 mb-1">Título do Passo</label>
                                      <input
                                        type="text"
                                        value={step.title}
                                        onChange={(e) => updateStep(sIdx, 'title', e.target.value)}
                                        placeholder="Ex: Identificação Inicial"
                                        className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-slate-600 mb-1">Descrição Curta (Opcional)</label>
                                      <input
                                        type="text"
                                        value={step.description || ''}
                                        onChange={(e) => updateStep(sIdx, 'description', e.target.value)}
                                        placeholder="Ex: Dados gerais de contacto"
                                        className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                                      />
                                    </div>
                                  </div>

                                  {/* Lista de Campos do Passo */}
                                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-bold uppercase text-slate-500 tracking-wider">Perguntas / Campos do Passo</span>
                                      <button
                                        onClick={() => addField(sIdx)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-md"
                                      >
                                        <Plus size={14} /> Adicionar Pergunta
                                      </button>
                                    </div>

                                    {step.fields.length === 0 ? (
                                      <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg text-slate-400 text-xs">
                                        Nenhuma pergunta adicionada a este passo. Clique em "+ Adicionar Pergunta".
                                      </div>
                                    ) : (
                                      step.fields.map((field, fIdx) => (
                                        <div key={field.id || fIdx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                                          {/* Linha 1: Pergunta / Rótulo + Tipo */}
                                          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                                            <div className="sm:col-span-7">
                                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Pergunta / Rótulo</label>
                                              <input
                                                type="text"
                                                value={field.label}
                                                onChange={(e) => updateField(sIdx, fIdx, 'label', e.target.value)}
                                                placeholder="Ex: Qual é o seu e-mail?"
                                                className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-800"
                                              />
                                            </div>
                                            <div className="sm:col-span-5">
                                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Tipo de Campo</label>
                                              <select
                                                value={field.type}
                                                onChange={(e) => updateField(sIdx, fIdx, 'type', e.target.value as any)}
                                                className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs bg-white"
                                              >
                                                {FIELD_TYPES.map((ft) => (
                                                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>

                                          {/* Linha 2: Placeholder + Mapeamento + Obrigatoriedade */}
                                          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                                            <div className="sm:col-span-5">
                                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Placeholder (Texto de apoio)</label>
                                              <input
                                                type="text"
                                                value={field.placeholder || ''}
                                                onChange={(e) => updateField(sIdx, fIdx, 'placeholder', e.target.value)}
                                                placeholder="Ex: Digite aqui..."
                                                className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
                                              />
                                            </div>
                                            <div className="sm:col-span-4">
                                              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Mapeamento para Lead</label>
                                              <select
                                                value={field.targetField || ''}
                                                onChange={(e) => updateField(sIdx, fIdx, 'targetField', e.target.value)}
                                                className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs bg-white"
                                              >
                                                {TARGET_FIELDS.map((tf) => (
                                                  <option key={tf.value} value={tf.value}>{tf.label}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div className="sm:col-span-3 flex items-center gap-2 pt-4">
                                              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer">
                                                <input
                                                  type="checkbox"
                                                  checked={field.required ?? false}
                                                  onChange={(e) => updateField(sIdx, fIdx, 'required', e.target.checked)}
                                                  className="rounded text-indigo-600 h-4 w-4"
                                                />
                                                Obrigatório
                                              </label>
                                              <div className="ml-auto flex items-center gap-1">
                                                <button
                                                  onClick={() => moveField(sIdx, fIdx, 'up')}
                                                  disabled={fIdx === 0}
                                                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                                  title="Subir pergunta"
                                                >
                                                  <ArrowUp size={14} />
                                                </button>
                                                <button
                                                  onClick={() => moveField(sIdx, fIdx, 'down')}
                                                  disabled={fIdx === step.fields.length - 1}
                                                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                                  title="Descer pergunta"
                                                >
                                                  <ArrowDown size={14} />
                                                </button>
                                                <button
                                                  onClick={() => removeField(sIdx, fIdx)}
                                                  className="p-1 text-red-500 hover:text-red-700"
                                                  title="Remover pergunta"
                                                >
                                                  <Trash2 size={14} />
                                                </button>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Opções de Escolha (para select, multiselect, radio) */}
                                          {['select', 'multiselect', 'radio'].includes(field.type) && (
                                            <div className="pt-2 border-t border-slate-200/60">
                                              <label className="block text-[11px] font-semibold text-indigo-700 mb-1">
                                                Opções de Escolha (separadas por vírgula)
                                              </label>
                                              <input
                                                type="text"
                                                value={(field.options || []).join(', ')}
                                                onChange={(e) => updateFieldOptionsStr(sIdx, fIdx, e.target.value)}
                                                placeholder="Ex: Opção 1, Opção 2, Opção 3"
                                                className="w-full rounded-lg border border-indigo-200 bg-indigo-50/30 px-3 py-1.5 text-xs text-slate-800"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* TAB 2: TEXTOS & CTA */}
                    {activeTab === 'content' && (
                      <div className="space-y-4">
                        <h4 className="font-bold text-slate-800 text-base mb-2">Textos do Cabeçalho & Botão</h4>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Nome interno do formulário</label>
                          <input
                            value={draft.name || ''}
                            onChange={(e) => updateDraft('name', e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Título público</label>
                          <input
                            value={draft.title || ''}
                            onChange={(e) => updateDraft('title', e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Subtítulo público</label>
                          <input
                            value={draft.subtitle || ''}
                            onChange={(e) => updateDraft('subtitle', e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Texto do botão final (CTA)</label>
                          <input
                            value={draft.ctaText || ''}
                            onChange={(e) => updateDraft('ctaText', e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {/* TAB 3: APARÊNCIA & CORES */}
                    {activeTab === 'design' && (
                      <div className="space-y-5">
                        {/* Layout */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Template Visual</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {DESIGNS.map((d) => (
                              <button
                                key={d.value}
                                onClick={() => updateDesign('templateId', d.value)}
                                className={`p-3 rounded-xl border text-left transition ${
                                  draft.design?.templateId === d.value
                                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <span className="block text-sm font-semibold text-slate-700">{d.label}</span>
                                <span className="block text-xs text-slate-500 mt-0.5">{d.desc}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Cores */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Cores do Tema</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Cor principal (botões de avanço)</label>
                              <input
                                type="color"
                                value={draft.design?.primaryColor || '#6366f1'}
                                onChange={(e) => updateDesign('primaryColor', e.target.value)}
                                className="w-full h-10 rounded-lg border border-slate-300 cursor-pointer bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Cor de destaque (botão submissão)</label>
                              <input
                                type="color"
                                value={draft.design?.accentColor || '#10b981'}
                                onChange={(e) => updateDesign('accentColor', e.target.value)}
                                className="w-full h-10 rounded-lg border border-slate-300 cursor-pointer bg-white"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Estilos */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Cantos</label>
                            <select
                              value={draft.design?.radius || 'rounded-xl'}
                              onChange={(e) => updateDesign('radius', e.target.value as any)}
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            >
                              {RADII.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Estilo dos campos</label>
                            <select
                              value={draft.design?.fieldStyle || 'filled'}
                              onChange={(e) => updateDesign('fieldStyle', e.target.value as any)}
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            >
                              {FIELD_STYLES.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={draft.design?.showLogo ?? true}
                            onChange={(e) => updateDesign('showLogo', e.target.checked)}
                            className="h-4 w-4 text-indigo-600 rounded"
                          />
                          Mostrar logo da empresa no cabeçalho
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Footer de publicação */}
              <div className="p-5 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3 bg-slate-50/50 rounded-b-2xl">
                <div className="text-sm text-slate-500">
                  {editing.published
                    ? <span className="inline-flex items-center gap-1.5 text-green-700 font-medium"><Globe size={15} /> Publicado</span>
                    : <span className="inline-flex items-center gap-1.5 text-slate-500"><EyeOff size={15} /> Rascunho — ainda não visível</span>}
                </div>
                <div className="flex items-center gap-2">
                  {editing.published ? (
                    <button
                      onClick={() => handleUnpublish(editing)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
                    >
                      <EyeOff size={16} /> Despublicar
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePublish(editing)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition"
                    >
                      <Globe size={16} /> Publicar
                    </button>
                  )}
                  {editing.slug && (
                    <button
                      onClick={() => copyLink(editing.slug)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
                    >
                      {copiedSlug === editing.slug ? <Check size={16} /> : <Copy size={16} />} Copiar link
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};